import { useHistory } from 'react-router-dom';
import { useContext, useRef } from 'react';
import { IView } from '../views/view';
import useSessionStorage from './useSessionStorage';
import { AppContext } from '../contexts/appContext';
import { ViewContext } from '../contexts/viewContext';
import parseUri from '../utils/parseUri';
import currentPartner, { isEmbeddable } from '../constants/partners';
import { logDebug } from '../utils/remoteLogger';

// Disable the automatic scroll restoration as it doesn't work
// properly when content is loaded asynchronously.
if (window.history.scrollRestoration) {
  window.history.scrollRestoration = 'manual';
}

interface INavigationOptions {
  replace?: boolean;
}

interface IPostNavigationOptions {
  persistRequests?: boolean;
  internalUrl?: string;
  delta?: number;
}

// As we can't pass data through browser history when navigating backwards,
// this is a temporary container for passing options for that use case.
let postNavigationOptions: IPostNavigationOptions = {};

interface NavigationInterface {
  push: (view: IView) => void;
  present: (view: IView) => void;
  replace: (view: IView) => void;
  replaceWithModal: (view: IView) => void;
  redirectToPath: (path: string, includeRedirect?: boolean) => void;
  pop: (postNavOptions?: IPostNavigationOptions) => void;
  dismiss: (postNavOptions?: IPostNavigationOptions) => void;
  canPop: () => boolean;
  reinitializeApp: (internalUrl?: string) => void;
  getCurrentView: () => IView;
  getLocationKey: () => string | undefined;
  getPostNavigationOptions: () => IPostNavigationOptions;
  clearPostNavigationOptions: () => void;
}

const useNavigation = (): NavigationInterface => {
  const { reinitializeApp } = useContext(AppContext);
  const { mainElement } = useContext(ViewContext);
  const history = useHistory();

  const cache = useSessionStorage<number>('SCROLL_POSITION', {
    locationAware: true,
  });

  const handleNavigationError = (error: unknown, action: string) => {
    logDebug(
      'navigation',
      `Failed during ${action}`,
      error instanceof Error ? error : new Error(String(error))
    );
  };

  const getViewStack = () => {
    return history.location.state as Array<IView>;
  };

  const getClosestModalRootIndex = (viewStack: Array<IView>) => {
    // Check if the current route is a modal. If that's not the case,
    // loop through the view stack and find the index of the closest modal.
    let closestModalIndex = -1;
    viewStack?.forEach((view, i) => {
      if (view?.isModalRoot) {
        closestModalIndex = i;
      }
    });
    return closestModalIndex;
  };

  const navigate = (view: IView, options?: INavigationOptions) => {
    try {
      // Reset post-navigation options.
      postNavigationOptions = {};

      // Store current scroll position.
      cache[1](window.scrollY || mainElement?.scrollTop || 0);

      const url = history.location.pathname + history.location.search;
      const currentStack = getViewStack() || [];
      const isModalDescendant = getClosestModalRootIndex(currentStack) > -1;

      if (options?.replace) {
        currentStack.pop();
        history.replace(url, [...currentStack, { ...view, isModalDescendant }]);
      } else {
        history.push(url, [...currentStack, { ...view, isModalDescendant }]);
      }
    } catch (error) {
      handleNavigationError(error, 'navigation');
    }
  };

  const push = (view: IView) => {
    return navigate(view);
  };

  const present = (view: IView) => {
    return navigate({ ...view, isModalRoot: true });
  };

  const replace = (view: IView) => {
    return navigate(view, { replace: true });
  };

  const replaceWithModal = (view: IView) => {
    return navigate({ ...view, isModalRoot: true }, { replace: true });
  };

  const redirectToPath = (path: string, includeRedirect?: boolean) => {
    try {
      const params = new URLSearchParams(history.location.search);
      const redirect = params.get('redirect');
      const shouldIncludeRedirect = includeRedirect && redirect;
      const url = shouldIncludeRedirect
        ? `${path}?redirect=${redirect ? encodeURIComponent(redirect) : ''}`
        : path;

      const isCallPath = path.includes('call');
      const isAppLink = history.location.pathname.includes('/app-link/');

      let parsedUrl;
      try {
        parsedUrl = parseUri(url);
        if (!parsedUrl) {
          logDebug('redirectToPath parsedUrl', `Failed to parse URL`);
        }
      } catch (err) {
        logDebug(
          'redirectToPath parsedUrl',
          `Invalid URL format: ${url}. Error: ${err}`
        );
      }

      if (parsedUrl?.host === 'post_message') {
        if (!isEmbeddable()) {
          logDebug(
            'postMessageAction',
            `Cannot postMessage to origin; ${currentPartner} not embeddable`
          );
          return;
        }

        try {
          const encodedCall = new URLSearchParams(parsedUrl.query || '').get(
            'payload'
          );
          if (!encodedCall) {
            logDebug(
              'postMessageAction',
              `Missing postMessage payload for ${url}`
            );
          }

          const data = encodedCall && JSON.parse(atob(encodedCall));
          if (!data) {
            logDebug(
              'postMessageAction',
              `Invalid postMessage payload for ${url}`
            );
            return;
          }

          const targetOrigin = '*';

          logDebug(
            'postMessageAction',
            `Will post message to origin ${targetOrigin}}`
          );
          window.parent.postMessage(data, targetOrigin);
        } catch (err) {
          logDebug(
            'postMessageAction',
            `Failed to process postMessage action: ${err}`
          );
        }
      }

      // Handle special cases for deep links (e.g., livi://flex?call=...) and call paths.
      if (!shouldIncludeRedirect && (isCallPath || isAppLink)) {
        if (parsedUrl?.host === 'flex') {
          const callParam = new URLSearchParams(parsedUrl.query || '').get(
            'call'
          );
          if (callParam) {
            history.push(`/flex?call=${callParam}`);
            return;
          }
        }

        if (isAppLink) {
          history.push(`/${parsedUrl?.host}?${parsedUrl?.query}`);
        }
        return;
      }

      // Default redirect
      history.replace(url);
    } catch (error) {
      handleNavigationError(error, 'redirectToPath');
    }
  };
  const pop = (postNavOptions?: IPostNavigationOptions) => {
    try {
      // Update post-navigation options.
      postNavigationOptions = postNavOptions || {};

      const delta = postNavigationOptions?.delta || 1;
      const stackSize = getViewStack()?.length || 1;

      if (delta < stackSize) {
        history.go(delta * -1);
        return;
      }
    } catch (err) {
      handleNavigationError(err, 'pop navigation');
    }

    reinitializeApp(postNavigationOptions?.internalUrl);
  };

  const dismiss = (postNavOptions?: IPostNavigationOptions) => {
    try {
      // Update post-navigation options.
      postNavigationOptions = postNavOptions || {};

      const viewStack = getViewStack();
      const closestModalIndex = getClosestModalRootIndex(viewStack);

      // Amount of navigation steps to dismiss
      const targetNavigationLength = viewStack?.length - closestModalIndex;

      if (closestModalIndex > -1) {
        // If we're navigating out of bounds, relaunch the app.
        if (viewStack?.length - targetNavigationLength < 1) {
          reinitializeApp();
        } else if (viewStack) {
          history.go((viewStack?.length - closestModalIndex) * -1);
        }
      }
    } catch (err) {
      handleNavigationError(err, 'dismiss navigation');
    }
  };

  const canPop = () => {
    const stack = getViewStack();
    const closestModalIndex = getClosestModalRootIndex(stack);
    const currentViewIsNotModalRoot = closestModalIndex !== stack.length - 1;

    return stack.length > 1 && currentViewIsNotModalRoot;
  };

  const getPostNavigationOptions = () => {
    return postNavigationOptions;
  };

  const clearPostNavigationOptions = () => {
    postNavigationOptions = {};
  };

  const getCurrentView = () => {
    const stack = getViewStack();
    return stack && stack[stack.length - 1];
  };

  const getLocationKey = () => {
    return history.location.key;
  };

  return useRef({
    push,
    present,
    replace,
    replaceWithModal,
    redirectToPath,
    pop,
    dismiss,

    canPop,

    reinitializeApp,

    getCurrentView,
    getLocationKey,
    getPostNavigationOptions,
    clearPostNavigationOptions,
  }).current;
};

export default useNavigation;
